import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { useAuthStore } from '../stores/authStore'
import { API_CONFIG } from '../lib/config'
import { toast } from 'sonner'
import {
    Search,
    Filter,
    Package,
    Database,
    RefreshCw,
    Eye,
    User,
    MapPin,
    Truck,
    CreditCard,
    Banknote,
    ShoppingCart
} from 'lucide-react'
import type { Order, OrdersResponse } from '../types/order'

export default function OrdersPage() {
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(20)
    const [searchQuery, setSearchQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState<'all' | '1' | '2' | '3' | '4' | '5'>('all')
    const [dateFrom, setDateFrom] = useState('')
    const [dateTo, setDateTo] = useState('')
    const [isXmlSyncing, setIsXmlSyncing] = useState(false)
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
    const [isOrderModalOpen, setIsOrderModalOpen] = useState(false)

    const { accessToken } = useAuthStore()

    const { data, isLoading, error, refetch } = useQuery<OrdersResponse>({
        queryKey: ['orders', currentPage, pageSize, searchQuery, statusFilter, dateFrom, dateTo],
        queryFn: async () => {
            const params = new URLSearchParams({
                page: currentPage.toString(),
                size: pageSize.toString(),
                ...(searchQuery && { query: searchQuery }),
                ...(statusFilter !== 'all' && { status: statusFilter }),
                ...(dateFrom && { dateFrom }),
                ...(dateTo && { dateTo })
            })

            const response = await fetch(`${API_CONFIG.BASE_URL}/api/orders?${params}`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            })

            if (!response.ok) {
                throw new Error('Siparişler yüklenemedi')
            }

            return response.json()
        },
        enabled: !!accessToken
    })

    // XML Senkronizasyon fonksiyonu
    const handleXmlSync = async () => {
        if (!accessToken) return

        setIsXmlSyncing(true)
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/orders/sync-xml`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            })

            if (!response.ok) {
                throw new Error('XML senkronizasyon hatası')
            }

            const result = await response.json()
            console.log('XML Sync sonucu:', result)

            // Başarılı senkronizasyon sonrası verileri yenile
            await refetch()

            // Başarı mesajı göster
            toast.success(`XML senkronizasyon tamamlandı! ${result.total} sipariş işlendi.`)

        } catch (error) {
            console.error('XML Sync hatası:', error)
            const errorMessage = error instanceof Error ? error.message : 'Bilinmeyen hata'
            toast.error('XML senkronizasyon hatası: ' + errorMessage)
        } finally {
            setIsXmlSyncing(false)
        }
    }

    // Sipariş detayını görüntüle
    const handleViewOrder = (order: Order) => {
        setSelectedOrder(order)
        setIsOrderModalOpen(true)
    }

    // Arama formu submit
    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
        setCurrentPage(1)
    }

    // Sayfa değişikliği
    const handlePageChange = (page: number) => {
        setCurrentPage(page)
    }

    // Sayfa boyutu değişikliği
    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize)
        setCurrentPage(1)
    }

    // Hata durumu
    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
                <Card className="max-w-2xl mx-auto shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <CardHeader className="text-center">
                        <CardTitle className="text-red-600 text-2xl">Hata</CardTitle>
                        <CardDescription className="text-lg">
                            Siparişler yüklenirken bir hata oluştu: {error.message}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="text-center">
                        <Button onClick={() => refetch()} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                            <RefreshCw className="w-5 h-5 mr-2" />
                            Tekrar Dene
                        </Button>
                    </CardContent>
                </Card>
            </div>
        )
    }

    return (
        <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-xl shadow-lg border-b border-white/20 mb-8">
                <div className="w-full px-4 sm:px-8 lg:px-20 py-4 sm:py-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 bg-clip-text text-transparent">
                                Sipariş Yönetimi
                            </h1>
                            <p className="text-slate-600 mt-2 text-sm sm:text-base lg:text-lg">Sipariş takibi ve yönetimi</p>
                        </div>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 w-full sm:w-auto">
                                <div className="flex items-center space-x-2 sm:space-x-3 text-xs sm:text-sm text-slate-700 bg-white/70 px-3 sm:px-4 py-2 rounded-xl">
                                    <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                                    <span className="font-semibold">Toplam: {data?.stats?.totalOrders || 0}</span>
                                </div>
                                <div className="flex items-center space-x-2 sm:space-x-3 text-xs sm:text-sm text-slate-700 bg-white/70 px-3 sm:px-4 py-2 rounded-xl">
                                    <Banknote className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                                    <span className="font-semibold">Toplam: ₺{(Number(data?.stats?.totalAmount) || 0).toFixed(2)}</span>
                                </div>
                                <div className="flex items-center space-x-2 sm:space-x-3 text-xs sm:text-sm text-slate-700 bg-white/70 px-3 sm:px-4 py-2 rounded-xl">
                                    <Package className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                                    <span className="font-semibold">Yeni: {data?.stats?.statusBreakdown?.[1] || 0}</span>
                                </div>
                            </div>
                            <div className="flex gap-2 sm:gap-3">
                                <Button onClick={() => refetch()} variant="outline" size="sm" className="bg-white/60 hover:bg-white/80 border-white/20 text-xs sm:text-sm">
                                    <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                                    <span className="hidden sm:inline">Yenile</span>
                                    <span className="sm:hidden">Yenile</span>
                                </Button>
                                <Button
                                    onClick={handleXmlSync}
                                    variant="outline"
                                    size="sm"
                                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white border-0 shadow-lg text-xs sm:text-sm"
                                    disabled={isXmlSyncing}
                                >
                                    {isXmlSyncing ? (
                                        <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" />
                                    ) : (
                                        <Database className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                                    )}
                                    <span className="hidden sm:inline">{isXmlSyncing ? 'Senkronize Ediliyor...' : 'XML Senkronizasyon'}</span>
                                    <span className="sm:hidden">{isXmlSyncing ? 'Sync...' : 'XML'}</span>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="w-full px-4 sm:px-8 lg:px-20 py-4 sm:py-6">
                {/* Filtreler ve Arama */}
                <Card className="mb-6 sm:mb-8 shadow-xl border-0 bg-white/80 backdrop-blur-sm">
                    <CardHeader className="pb-3 sm:pb-4">
                        <CardTitle className="flex items-center text-lg sm:text-xl">
                            <Filter className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-blue-600" />
                            Filtreler ve Arama
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 sm:px-6 py-3 sm:py-4">
                        <form onSubmit={handleSearch} className="space-y-4">
                            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                                <div className="flex-1">
                                    <Label htmlFor="search" className="sr-only">Sipariş Ara</Label>
                                    <div className="relative group">
                                        <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                                        <Input
                                            id="search"
                                            type="text"
                                            placeholder="Sipariş no, email veya müşteri adı ara..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="pl-10 sm:pl-12 h-10 sm:h-12 bg-white/60 border-slate-200 focus:bg-white focus:border-blue-500 focus:ring-blue-500 transition-all duration-300 rounded-xl text-sm"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => { setStatusFilter(e.target.value as any); setCurrentPage(1) }}
                                        className="h-10 sm:h-12 px-3 rounded-xl border-slate-200 bg-white text-sm"
                                    >
                                        <option value="all">Tüm Durumlar</option>
                                        <option value="1">Yeni Sipariş</option>
                                        <option value="2">Hazırlanıyor</option>
                                        <option value="3">Kargoda</option>
                                        <option value="4">Teslim Edildi</option>
                                        <option value="5">İptal</option>
                                    </select>
                                    <Input
                                        type="date"
                                        value={dateFrom}
                                        onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1) }}
                                        className="h-10 sm:h-12 px-3 rounded-xl border-slate-200 bg-white text-sm"
                                        placeholder="Başlangıç Tarihi"
                                    />
                                    <Input
                                        type="date"
                                        value={dateTo}
                                        onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1) }}
                                        className="h-10 sm:h-12 px-3 rounded-xl border-slate-200 bg-white text-sm"
                                        placeholder="Bitiş Tarihi"
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                <Button type="submit" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 h-10 sm:h-12 px-4 sm:px-6 rounded-xl shadow-lg text-sm">
                                    <Search className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                                    Ara
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setSearchQuery('')
                                        setStatusFilter('all')
                                        setDateFrom('')
                                        setDateTo('')
                                        setCurrentPage(1)
                                    }}
                                    className="h-10 sm:h-12 px-4 sm:px-6 rounded-xl border-slate-200 hover:bg-slate-50 text-sm"
                                >
                                    Temizle
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>

                {/* Sipariş Listesi */}
                <Card className="shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <CardHeader className="px-4 sm:px-6 py-3 sm:py-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div>
                                <CardTitle className="text-xl sm:text-2xl">Sipariş Listesi</CardTitle>
                                <CardDescription className="text-sm sm:text-lg">
                                    {data?.pagination?.total ? `${((currentPage - 1) * pageSize) + 1} - ${Math.min(currentPage * pageSize, data.pagination.total)} arası, toplam ${data.pagination.total} sonuçtan` : 'Sipariş bulunamadı'}
                                </CardDescription>
                            </div>
                            <div className="flex items-center space-x-2 sm:space-x-4">
                                <div className="flex items-center space-x-2">
                                    <Label htmlFor="pageSize" className="text-xs sm:text-sm font-medium text-slate-700">Sayfa başına:</Label>
                                    <select
                                        id="pageSize"
                                        value={pageSize}
                                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                        className="border border-slate-300 rounded-lg px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm bg-white focus:border-blue-500 focus:ring-blue-500"
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="px-4 py-3">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-16">
                                <div className="text-center">
                                    <RefreshCw className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
                                    <span className="text-slate-600 text-lg">Siparişler yükleniyor...</span>
                                </div>
                            </div>
                        ) : data?.orders && data.orders.length > 0 ? (
                            <div className="overflow-x-auto w-full">
                                <table className="w-full table-auto min-w-[800px]">
                                    <thead>
                                        <tr className="border-b border-slate-200">
                                            <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm">Sipariş No</th>
                                            <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm">Müşteri</th>
                                            <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm hidden md:table-cell">Tarih</th>
                                            <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm">Durum</th>
                                            <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm">Toplam</th>
                                            <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm hidden lg:table-cell">Ürün Sayısı</th>
                                            <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm hidden xl:table-cell">Teslimat</th>
                                            <th className="text-left py-3 px-2 font-semibold text-slate-900 text-xs sm:text-sm">İşlemler</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.orders.map((order) => (
                                            <tr key={order.id} className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors duration-200">
                                                <td className="py-3 px-2">
                                                    <span className="font-mono text-xs sm:text-sm font-semibold text-blue-600">
                                                        {order.orderNumber}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-2">
                                                    <div className="text-xs sm:text-sm">
                                                        <div className="font-medium text-slate-900">{order.deliveryRecipient || '-'}</div>
                                                        <div className="text-xs text-slate-500">{order.email || '-'}</div>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-2 hidden md:table-cell">
                                                    <div className="text-xs sm:text-sm">
                                                        <div className="font-medium">{new Date(order.orderDate).toLocaleDateString('tr-TR')}</div>
                                                        <div className="text-xs text-slate-500">{order.orderTime || '-'}</div>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-2">
                                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${order.status === 1 ? 'bg-blue-100 text-blue-800' :
                                                        order.status === 2 ? 'bg-yellow-100 text-yellow-800' :
                                                            order.status === 3 ? 'bg-purple-100 text-purple-800' :
                                                                order.status === 4 ? 'bg-green-100 text-green-800' :
                                                                    'bg-red-100 text-red-800'
                                                        }`}>
                                                        {order.statusText}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-2">
                                                    <div className="text-xs sm:text-sm font-semibold text-slate-900">
                                                        ₺{Number(order.netTotal).toFixed(2)}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-2 hidden lg:table-cell">
                                                    <span className="text-xs sm:text-sm text-slate-700">
                                                        {order.orderItems.length} ürün
                                                    </span>
                                                </td>
                                                <td className="py-3 px-2 hidden xl:table-cell">
                                                    <div className="text-xs text-slate-600">
                                                        <div className="font-medium">{order.deliveryCity || '-'}</div>
                                                        <div>{order.deliveryDistrict || '-'}</div>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleViewOrder(order)}
                                                        className="text-blue-600 border-blue-200 hover:bg-blue-50 text-xs sm:text-sm px-2 sm:px-3"
                                                    >
                                                        <Eye className="w-4 h-4 mr-1" />
                                                        Detay
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-16">
                                <Package className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                                <p className="text-slate-600 text-lg">Sipariş bulunamadı</p>
                            </div>
                        )}

                        {/* Sayfalama */}
                        {data && data.pagination?.totalPages > 1 && (
                            <div className="flex items-center justify-between mt-8 pt-8 border-t border-slate-200">
                                <div className="text-sm text-slate-700">
                                    <span className="font-medium">
                                        Sayfa {currentPage} / {data.pagination.totalPages}
                                    </span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                                    >
                                        Önceki
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === data.pagination.totalPages}
                                        className="border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                                    >
                                        Sonraki
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Sipariş Detay Modal */}
            {isOrderModalOpen && selectedOrder && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="relative max-w-4xl max-h-[90vh] bg-white rounded-lg overflow-hidden">
                        <div className="absolute top-4 right-4 z-10">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setIsOrderModalOpen(false)}
                                className="bg-white/90 hover:bg-white border-slate-200"
                            >
                                ✕
                            </Button>
                        </div>
                        <div className="p-6 overflow-y-auto max-h-[90vh]">
                            <div className="mb-6">
                                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                                    Sipariş #{selectedOrder.orderNumber}
                                </h2>
                                <p className="text-slate-600">
                                    {new Date(selectedOrder.orderDate).toLocaleDateString('tr-TR')} - {selectedOrder.orderTime}
                                </p>
                            </div>

                            {/* Sipariş Bilgileri */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <Card>
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-lg flex items-center">
                                            <User className="w-5 h-5 mr-2 text-blue-600" />
                                            Müşteri Bilgileri
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-2">
                                        <div><strong>Ad Soyad:</strong> {selectedOrder.deliveryRecipient || '-'}</div>
                                        <div><strong>Email:</strong> {selectedOrder.email || '-'}</div>
                                        <div><strong>Telefon:</strong> {selectedOrder.deliveryPhone || '-'}</div>
                                        <div><strong>TC No:</strong> {selectedOrder.tcNo || '-'}</div>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-lg flex items-center">
                                            <MapPin className="w-5 h-5 mr-2 text-green-600" />
                                            Teslimat Adresi
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-2">
                                        <div className="text-sm">{selectedOrder.deliveryAddress || '-'}</div>
                                        <div><strong>Şehir:</strong> {selectedOrder.deliveryCity || '-'}</div>
                                        <div><strong>İlçe:</strong> {selectedOrder.deliveryDistrict || '-'}</div>
                                        <div><strong>Ülke:</strong> {selectedOrder.deliveryCountry || '-'}</div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Ödeme ve Kargo Bilgileri */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <Card>
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-lg flex items-center">
                                            <CreditCard className="w-5 h-5 mr-2 text-purple-600" />
                                            Ödeme Bilgileri
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-2">
                                        <div><strong>Ödeme Yöntemi:</strong> {selectedOrder.paymentMethod || '-'}</div>
                                        <div><strong>Banka:</strong> {selectedOrder.bank || '-'}</div>
                                        <div><strong>Toplam Tutar:</strong> ₺{Number(selectedOrder.netTotal).toFixed(2)}</div>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-lg flex items-center">
                                            <Truck className="w-5 h-5 mr-2 text-orange-600" />
                                            Kargo Bilgileri
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-2">
                                        <div><strong>Kargo Firması:</strong> {selectedOrder.shippingCompany || '-'}</div>
                                        <div><strong>Takip Kodu:</strong> {selectedOrder.trackingCode || '-'}</div>
                                        <div><strong>Kargo Ödemesi:</strong> {selectedOrder.shippingPayment || '-'}</div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Ürün Listesi */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center">
                                        <Package className="w-5 h-5 mr-2 text-indigo-600" />
                                        Sipariş Ürünleri ({selectedOrder.orderItems.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {selectedOrder.orderItems.map((item) => (
                                            <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                                <div className="flex-1">
                                                    <div className="font-medium text-slate-900">{item.productName}</div>
                                                    <div className="text-sm text-slate-600">
                                                        Kod: {item.code} | Miktar: {item.quantity} {item.unit}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-semibold text-slate-900">
                                                        ₺{Number(item.price).toFixed(2)}
                                                    </div>
                                                    <div className="text-sm text-slate-600">
                                                        KDV: {item.tax}%
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
